import * as _ from 'lodash';

import { hasTranslation } from '../languageUtil';
import { getVHSValue } from '../../CornerstoneTools/VHSTool';
import {
  auxiliaryPredictions,
  newline,
  tab,
  bulletPointLevel1,
  bulletPointLevel2,
  PRESENT,
  PROBABLE,
  ABSENT,
  DIFFERENTIAL_DIAGNOSIS,
  OPACITIES,
  MARQUE,
} from './constants';
import { postProcessPredictions } from '../predictions/postProcessPredictions';
import { isIn, getSeveriteInt, addToMaxSeverity, haveSameSeverity } from './utils';
import GenericPatternsReportGenerator from './GenericPatternsReportGenerator';
import {
  GLOBAL_THRESHOLD_FOR_SECURE,
  COMMON_PATTERNS_DESCRIPTION,
  THORAX_GROUPS,
  THORAX_PATTERNS,
  THORAX_THRESHOLD_FOR_DOUBTFUL,
} from 'app/components/PatternStructuredList/patternsGroups';
import { ImageForReport, PatternRepresentation } from 'app/utils/reports/types';
import { IntlShape } from 'react-intl';
import { getFirstAnnotationValue } from 'app/utils/reports/getFirstAnnotationValue';
import { te } from 'date-fns/locale';

class ThoraxReportGenerator extends GenericPatternsReportGenerator {
  generateReport = (imagesInCase: ImageForReport[]) => {
    const returned = this.getMaxPredictions(imagesInCase);
    const { maxPredictionsFeedback, maxPredictions } = returned;
    // maxPredictions.cardiomegalie = 0.8
    // maxPredictions.scissure_interlobaire = 0.4
    // maxPredictions.epanchement_pleural = 0.
    // maxPredictions.opacite_bronchique = 0.8
    // maxPredictions.opacite_interstitielle = 0.8
    // maxPredictions.opacite_alveolaire = 0.4
    // maxPredictions.cardiomegalie = 0.8
    // maxPredictions.dilatation_ventricule_droit = 0.4
    const numberOfPatternsDetected = this.getNumberOfPatternsToDisplay(
      maxPredictions,
      maxPredictionsFeedback,
      THORAX_PATTERNS
    );
    let report = `${this.formatMessage('thorax').toUpperCase()} :${newline}${newline}`;

    report += this.getVHSReport(imagesInCase);

    const postProcessedPredictions = postProcessPredictions(maxPredictions);

    report += this.getShortCompteRendu(postProcessedPredictions, maxPredictionsFeedback, {
      ...COMMON_PATTERNS_DESCRIPTION,
      ...THORAX_PATTERNS,
    });

    /** @deprecated
     * Il faut retravailler cette partie pour l'adapter au futur format de rapport
     */
    // if (numberOfPatternsDetected === 0) {
    //   report += this.formatMessage('report.thorax.isOk');
    // } else if (numberOfPatternsDetected <= 2) {
    //   report += this.formatMessage('report.thorax.restIsOk');
    // }

    report += newline + newline;
    return report;
  };

  getVHSReport = (images: ImageForReport[]) => {
    const VHSValues = images
      .filter((image) => !_.isEmpty(_.get(image, 'annotations.VHS', {})))
      .map((image) => {
        const vhsData = getFirstAnnotationValue(image.annotations.VHS);
        return getVHSValue(vhsData, 1);
      });

    if (VHSValues.length === 0) return '';

    let report = `${this.formatMessage('report.measure.vhs')} = `;
    VHSValues.forEach((v, i) => {
      report += `${i === 0 ? '' : '; '}${this.formatNumber(v, { style: 'decimal' })} V`;
    });

    return report + newline;
  };

  composeCardiovasculaire = (
    markedPatterns: PatternRepresentation[],
    presentPatterns: PatternRepresentation[],
    likelyPatterns: PatternRepresentation[],
    unlikelyPatterns: PatternRepresentation[]
  ) => {
    let patternsToRemove: string[] = [];
    if (isIn([markedPatterns, presentPatterns, likelyPatterns], 'cardiomegalie')) {
      if (
        isIn([markedPatterns, presentPatterns, likelyPatterns], 'dilatation_ventricule_droit') &&
        isIn([markedPatterns, presentPatterns, likelyPatterns], 'dilatation_ventricule_gauche')
      ) {
        addToMaxSeverity(
          'cardiomegalie globale',
          ['cardiomegalie', 'dilatation_ventricule_droit'],
          [markedPatterns, presentPatterns, likelyPatterns]
        );
        patternsToRemove = [
          'dilatation_ventricule_gauche',
          'dilatation_ventricule_droit',
          'cardiomegalie',
          'dilatation_atrium_gauche',
        ];
      } else if (
        isIn([markedPatterns, presentPatterns, likelyPatterns], 'dilatation_ventricule_droit')
      ) {
        addToMaxSeverity(
          'cardiomegalie droite',
          ['cardiomegalie', 'dilatation_ventricule_droit'],
          [markedPatterns, presentPatterns, likelyPatterns]
        );
        patternsToRemove = ['dilatation_ventricule_droit', 'cardiomegalie'];
      } else if (isIn([unlikelyPatterns], 'dilatation_ventricule_droit')) {
        addToMaxSeverity(
          'cardiomegalie droite suspicion',
          ['cardiomegalie', 'dilatation_ventricule_droit'],
          [markedPatterns, presentPatterns, likelyPatterns]
        );
        patternsToRemove = ['dilatation_ventricule_droit', 'cardiomegalie'];
      } else if (
        isIn([markedPatterns, presentPatterns, likelyPatterns], 'dilatation_ventricule_gauche')
      ) {
        const isDilatationAtriumGauche = isIn(
          [markedPatterns, presentPatterns],
          'dilatation_atrium_gauche'
        );
        const dilatationAtriumGauche = isDilatationAtriumGauche ? ' avec dilatation atriale' : '';
        const dilatationAtriumGaucheList = isDilatationAtriumGauche
          ? ['dilatation_atrium_gauche']
          : [];
        addToMaxSeverity(
          `cardiomegalie gauche${dilatationAtriumGauche}`,
          ['cardiomegalie', 'dilatation_ventricule_gauche'],
          [markedPatterns, presentPatterns, likelyPatterns]
        );
        patternsToRemove = [
          'dilatation_ventricule_gauche',
          'cardiomegalie',
          ...dilatationAtriumGaucheList,
        ];
      } else if (isIn([unlikelyPatterns], 'dilatation_ventricule_gauche')) {
        const isDilatationAtriumGauche = isIn(
          [markedPatterns, presentPatterns],
          'dilatation_atrium_gauche'
        );
        const dilatationAtriumGauche = isDilatationAtriumGauche ? ' avec dilatation atriale' : '';
        const dilatationAtriumGaucheList = isDilatationAtriumGauche
          ? ['dilatation_atrium_gauche']
          : [];
        addToMaxSeverity(
          `cardiomegalie gauche suspicion${dilatationAtriumGauche}`,
          ['cardiomegalie', 'dilatation_ventricule_gauche'],
          [markedPatterns, presentPatterns, likelyPatterns]
        );
        patternsToRemove = [
          'dilatation_ventricule_gauche',
          'cardiomegalie',
          ...dilatationAtriumGaucheList,
        ];
      }
    }
    return patternsToRemove;
  };

  composeOpaciteMixte = (
    markedPatterns: PatternRepresentation[],
    presentPatterns: PatternRepresentation[],
    likelyPatterns: PatternRepresentation[],
    unlikelyPatterns: PatternRepresentation[]
  ) => {
    let patternsToRemove: string[] = [];
    const severiteAlveolaire = getSeveriteInt(
      'opacite_alveolaire',
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
    const severiteInterstielle = getSeveriteInt(
      'opacite_interstitielle',
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
    const severiteBronchique = getSeveriteInt(
      'opacite_bronchique',
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
    if (
      severiteInterstielle > ABSENT &&
      severiteAlveolaire > ABSENT &&
      severiteBronchique > ABSENT
    ) {
      if (
        severiteAlveolaire === severiteInterstielle &&
        severiteInterstielle === severiteBronchique
      ) {
        addToMaxSeverity(
          'opacite mixte',
          ['opacite_alveolaire', 'opacite_interstitielle', 'opacite_bronchique'],
          [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle', 'opacite_bronchique'];
      } else if (severiteAlveolaire === severiteInterstielle) {
        addToMaxSeverity(
          'opacite alveolo-interstitielle',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
        );
        this.join('opacite_bronchique', 'opacite alveolo-interstitielle', [
          markedPatterns,
          presentPatterns,
          likelyPatterns,
          unlikelyPatterns,
        ]);
        patternsToRemove = [
          'opacite_alveolaire',
          'opacite_interstitielle',
          'opacite_bronchique',
          'opacite alveolo-interstitielle',
        ];
      } else if (severiteInterstielle === severiteBronchique) {
        addToMaxSeverity(
          'opacite broncho-interstitielle',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
        );
        this.join('opacite_alveolaire', 'opacite broncho-interstitielle', [
          markedPatterns,
          presentPatterns,
          likelyPatterns,
          unlikelyPatterns,
        ]);
        patternsToRemove = [
          'opacite_alveolaire',
          'opacite_interstitielle',
          'opacite_bronchique',
          'opacite broncho-interstitielle',
        ];
      } else {
        let domite;
        if (
          severiteAlveolaire === severiteBronchique &&
          severiteAlveolaire > severiteInterstielle
        ) {
          domite = 'report.domites.alveolaire et bronchique';
        } else if (severiteInterstielle > Math.max(severiteBronchique, severiteAlveolaire)) {
          domite = 'report.domites.interstitielle';
        } else if (severiteBronchique > Math.max(severiteInterstielle, severiteAlveolaire)) {
          domite = 'report.domites.bronchique';
        } else if (severiteAlveolaire > Math.max(severiteBronchique, severiteInterstielle)) {
          domite = 'report.domites.alveolaire';
        }
        const textToAdd = this.formatMessage(domite);
        addToMaxSeverity(
          textToAdd,
          ['opacite_alveolaire', 'opacite_interstitielle', 'opacite_bronchique'],
          [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
        );
        patternsToRemove = [
          'opacite_alveolaire',
          'opacite_interstitielle',
          'opacite_bronchique',
          'opacite broncho-interstitielle',
        ];
      }
    }

    return patternsToRemove;
  };

  composeBronchoInterstitiel = (
    markedPatterns: PatternRepresentation[],
    presentPatterns: PatternRepresentation[],
    likelyPatterns: PatternRepresentation[],
    unlikelyPatterns: PatternRepresentation[]
  ) => {
    let patternsToRemove: string[] = [];
    if (
      haveSameSeverity(
        ['opacite_bronchique', 'opacite_interstitielle'],
        [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
      )
    ) {
      addToMaxSeverity(
        'opacite broncho-interstitielle',
        ['opacite_bronchique', 'opacite_interstitielle'],
        [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
      );
      patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
    } else if (isIn([markedPatterns], 'opacite_bronchique')) {
      if (isIn([presentPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite bronchique a interstitielle',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
      } else if (isIn([likelyPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite bronchique a interstitielle probable',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
      } else if (isIn([unlikelyPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite bronchique a interstitielle suspicion',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
      }
    } else if (isIn([presentPatterns], 'opacite_bronchique')) {
      if (isIn([markedPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite interstitielle a bronchique',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
      } else if (isIn([likelyPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite bronchique presente et interstitielle probable',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [presentPatterns]
        );
        patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
      } else if (isIn([unlikelyPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite bronchique presente et interstitielle suspicion',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [presentPatterns]
        );
        patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
      }
    } else if (isIn([likelyPatterns], 'opacite_bronchique')) {
      if (isIn([markedPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite interstitielle a bronchique probable',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
      } else if (isIn([presentPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite interstitielle presente et bronchique probable',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [presentPatterns]
        );
        patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
      }
    } else if (isIn([unlikelyPatterns], 'opacite_bronchique')) {
      if (isIn([markedPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite interstitielle a bronchique suspicion',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
      } else if (isIn([presentPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite interstitielle presente et bronchique suspicion',
          ['opacite_bronchique', 'opacite_interstitielle'],
          [presentPatterns]
        );
        patternsToRemove = ['opacite_bronchique', 'opacite_interstitielle'];
      }
    }
    return patternsToRemove;
  };

  composeAlveoloInterstitiel = (
    markedPatterns: PatternRepresentation[],
    presentPatterns: PatternRepresentation[],
    likelyPatterns: PatternRepresentation[],
    unlikelyPatterns: PatternRepresentation[]
  ) => {
    let patternsToRemove: string[] = [];
    if (
      haveSameSeverity(
        ['opacite_alveolaire', 'opacite_interstitielle'],
        [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
      )
    ) {
      addToMaxSeverity(
        'opacite alveolo-interstitielle',
        ['opacite_alveolaire', 'opacite_interstitielle'],
        [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
      );
      patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
    } else if (isIn([markedPatterns], 'opacite_alveolaire')) {
      if (isIn([presentPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite alveolaire a interstitielle',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
      } else if (isIn([likelyPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite alveolaire a interstitielle probable',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
      } else if (isIn([unlikelyPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite alveolaire a interstitielle suspicion',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
      }
    } else if (isIn([presentPatterns], 'opacite_alveolaire')) {
      if (isIn([markedPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite interstitielle a alveolaire',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
      } else if (isIn([likelyPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite alveolaire presente et interstitielle probable',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [presentPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
      } else if (isIn([unlikelyPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite alveolaire presente et interstitielle suspicion',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [presentPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
      }
    } else if (isIn([likelyPatterns], 'opacite_alveolaire')) {
      if (isIn([markedPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite interstitielle a alveolaire probable',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
      } else if (isIn([presentPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite interstitielle presente et alveolaire probable',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [presentPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
      }
    } else if (isIn([unlikelyPatterns], 'opacite_alveolaire')) {
      if (isIn([markedPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite interstitielle a alveolaire suspicion',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [markedPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
      } else if (isIn([presentPatterns], 'opacite_interstitielle')) {
        addToMaxSeverity(
          'opacite interstitielle presente et alveolaire suspicion',
          ['opacite_alveolaire', 'opacite_interstitielle'],
          [presentPatterns]
        );
        patternsToRemove = ['opacite_alveolaire', 'opacite_interstitielle'];
      }
    }
    return patternsToRemove;
  };

  composePulmonaire = (
    markedPatterns: PatternRepresentation[],
    presentPatterns: PatternRepresentation[],
    likelyPatterns: PatternRepresentation[],
    unlikelyPatterns: PatternRepresentation[]
  ) => {
    let patternsToRemove = this.composeOpaciteMixte(
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
    if (patternsToRemove.length === 0) {
      patternsToRemove.push(
        ...this.composeBronchoInterstitiel(
          markedPatterns,
          presentPatterns,
          likelyPatterns,
          unlikelyPatterns
        )
      );
      patternsToRemove.push(
        ...this.composeAlveoloInterstitiel(
          markedPatterns,
          presentPatterns,
          likelyPatterns,
          unlikelyPatterns
        )
      );
    }
    return patternsToRemove;
  };

  composePleural = (
    markedPatterns: PatternRepresentation[],
    presentPatterns: PatternRepresentation[],
    likelyPatterns: PatternRepresentation[],
    unlikelyPatterns: PatternRepresentation[]
  ) => {
    let patternsToRemove: string[] = [];
    const severiteEpanchement = getSeveriteInt(
      'epanchement_pleural',
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
    const severiteScissures = getSeveriteInt(
      'scissure_interlobaire',
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
    if (severiteEpanchement >= PRESENT) {
      patternsToRemove.push('scissure_interlobaire');
    } else if (severiteEpanchement > ABSENT) {
      if (severiteScissures >= PRESENT) {
        addToMaxSeverity(
          'scissures et epanchement',
          ['scissure_interlobaire', 'epanchement_pleural'],
          [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
        );
        patternsToRemove = ['scissure_interlobaire', 'epanchement_pleural'];
      } else if (severiteScissures > ABSENT) {
        addToMaxSeverity(
          'scissures probables et epanchement',
          ['scissure_interlobaire', 'epanchement_pleural'],
          [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
        );
        patternsToRemove = ['scissure_interlobaire', 'epanchement_pleural'];
      }
    } else if (severiteEpanchement === ABSENT) {
      if (severiteScissures > ABSENT) {
        addToMaxSeverity(
          'scissures sans epanchement',
          ['scissure_interlobaire', 'epanchement_pleural'],
          [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
        );
        patternsToRemove = ['scissure_interlobaire', 'epanchement_pleural'];
      }
    }
    return patternsToRemove;
  };

  composeDeviationTrachealeCardiomegalie = (
    markedPatterns: PatternRepresentation[],
    presentPatterns: PatternRepresentation[],
    likelyPatterns: PatternRepresentation[],
    unlikelyPatterns: PatternRepresentation[]
  ) => {
    const severiteDeviationTracheale = getSeveriteInt(
      'deviation_tracheale',
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
    let patternsToRemove: string[] = [];
    if (severiteDeviationTracheale > ABSENT) {
      // addToMaxSeverity("deviation tracheale dorsale", ["deviation_tracheale", "cardiomegalie"], [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns])
      this.associate('cardiomegalie', 'deviation_tracheale', 'deviation tracheale dorsale', [
        markedPatterns,
        presentPatterns,
        likelyPatterns,
        unlikelyPatterns,
      ]);
      patternsToRemove = ['cardiomegalie', 'deviation_tracheale'];
    }
    return patternsToRemove;
  };

  composeDeviationTrachealeDilatationOesophage = (
    markedPatterns: PatternRepresentation[],
    presentPatterns: PatternRepresentation[],
    likelyPatterns: PatternRepresentation[],
    unlikelyPatterns: PatternRepresentation[]
  ) => {
    const severiteDeviationTracheale = getSeveriteInt(
      'deviation_tracheale',
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
    let patternsToRemove: string[] = [];
    if (severiteDeviationTracheale > ABSENT) {
      // addToMaxSeverity("deviation tracheale ventrale", ["deviation_tracheale", "dilatation_oesophagienne"], [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns])
      this.associate(
        'dilatation_oesophagienne',
        'deviation_tracheale',
        'deviation tracheale ventrale',
        [markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns]
      );
      patternsToRemove = ['dilatation_oesophagienne', 'deviation_tracheale'];
    }
    return patternsToRemove;
  };

  composeTrachee = (
    markedPatterns: PatternRepresentation[],
    presentPatterns: PatternRepresentation[],
    likelyPatterns: PatternRepresentation[],
    unlikelyPatterns: PatternRepresentation[]
  ) => {
    const severiteCardiomegalie = getSeveriteInt(
      'cardiomegalie',
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
    const severiteDilatationOesophage = getSeveriteInt(
      'dilatation_oesophagienne',
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
    let patternsToRemove: string[] = [];
    if (severiteCardiomegalie >= severiteDilatationOesophage && severiteCardiomegalie >= PROBABLE) {
      patternsToRemove = this.composeDeviationTrachealeCardiomegalie(
        markedPatterns,
        presentPatterns,
        likelyPatterns,
        unlikelyPatterns
      );
    } else if (severiteDilatationOesophage >= PROBABLE) {
      patternsToRemove = this.composeDeviationTrachealeDilatationOesophage(
        markedPatterns,
        presentPatterns,
        likelyPatterns,
        unlikelyPatterns
      );
    }
    return patternsToRemove;
  };

  composePatterns = (
    markedPatterns: PatternRepresentation[],
    presentPatterns: PatternRepresentation[],
    likelyPatterns: PatternRepresentation[],
    unlikelyPatterns: PatternRepresentation[]
  ) => {
    let patternsToRemove: string[] = [];
    patternsToRemove.push(
      ...this.composeCardiovasculaire(
        markedPatterns,
        presentPatterns,
        likelyPatterns,
        unlikelyPatterns
      ),
      ...this.composePulmonaire(markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns),
      ...this.composeTrachee(markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns),
      ...this.composePleural(markedPatterns, presentPatterns, likelyPatterns, unlikelyPatterns)
    );
    return this.removePatterns(
      patternsToRemove,
      markedPatterns,
      presentPatterns,
      likelyPatterns,
      unlikelyPatterns
    );
  };

  /**
   * Generates Report as a List
   * @param imagesInCase
   * @returns string
   */
  generateList = (imagesInCase: ImageForReport[]) => {
    const returned = this.getMaxPredictions(imagesInCase);
    const { maxPredictionsFeedback, maxPredictions } = returned;
    const numberOfPatternsDetected = this.getNumberOfPatternsToDisplay(
      maxPredictions,
      maxPredictionsFeedback,
      THORAX_PATTERNS
    );

    // const hasVHSReport = this.getVHSReport(imagesInCase).trimEnd() === '' ? false : true;
    // if (numberOfPatternsDetected === 0 && !hasVHSReport) {
    //   return '';
    // }

    /* THORAX TITLE */
    let list = this.formatMessage('thorax').toUpperCase() + ' :' + newline;

    /** @deprecated
     * Il faut retravailler cette partie pour l'adapter au futur format de rapport
     */
    // if (numberOfPatternsDetected === 0) {
    //   list += newline + tab + this.formatMessage('report.thorax.isOk');
    // }

    const groups = THORAX_GROUPS;
    Object.keys(groups).forEach((group: keyof typeof groups) => {
      const patterns = groups[group].patterns;
      const differentialDiagnosis = _.get(groups[group], 'differentialDiagnosis', []);

      /* GROUP TITLE */
      let groupTitle = newline + tab + this.formatMessage(group);
      let countPartterns = 0;
      let partternsLines = '';
      let patternsMarques: string[] = [];
      let patternsPresents: string[] = [];

      patterns.forEach((pattern: string) => {
        if (maxPredictions[pattern] > THORAX_THRESHOLD_FOR_DOUBTFUL) {
          countPartterns++;
          if (maxPredictions[pattern] >= GLOBAL_THRESHOLD_FOR_SECURE) {
            patternsMarques.push(pattern);
          } else {
            patternsPresents.push(pattern);
          }
        }
      });

      /* RIEN À SIGNALER */
      if (countPartterns === 0) {
        // title += ' ' + this.formatMessage('patternStructuredList.noPatternInThisGroup');

        // Cas spécial du VHS
        if (group === 'Système Cardio-Vasculaire') {
          /* Titre Zone */
          list += groupTitle;
        }
      } else {
        /* Titre Zone */
        list += groupTitle;
      }

      /* VHS */
      if (group === 'Système Cardio-Vasculaire') {
        list += newline + tab + tab + bulletPointLevel1 + this.getVHSReport(imagesInCase).trimEnd();
      }

      /* Présence Marquée >= 75% */
      if (patternsMarques.length) {
        patternsMarques.forEach((pattern) => {
          partternsLines +=
            newline +
            tab +
            tab +
            bulletPointLevel1 +
            this.formatMessage(`report.pathology.list.thorax.${pattern}`);
        });
      }

      /* Présence > 45% & <= 75% */
      if (patternsPresents.length) {
        patternsPresents.forEach((pattern) => {
          partternsLines +=
            newline +
            tab +
            tab +
            bulletPointLevel1 +
            this.formatMessage(`report.pathology.list.thorax.${pattern}`);
        });
      }
      list += partternsLines + newline;

      /* Differential Diagnosis */
      if (differentialDiagnosis.length) {
        let patternsDD: string[] = [];

        differentialDiagnosis.forEach((pattern: string) => {
          if (maxPredictions[pattern] > THORAX_THRESHOLD_FOR_DOUBTFUL) {
            patternsDD.push(pattern);
          }
        });
        if (patternsDD.length) {
          const titleDD =
            patternsPresents.length + patternsMarques.length > 1 ? 'plural' : 'singular';
          list +=
            newline +
            tab +
            tab +
            this.formatMessage(`report.pathology.list.differential_diagnosis.${titleDD}`);

          patternsDD.forEach((pattern) => {
            list += newline + tab + tab + tab + bulletPointLevel2 + this.formatMessage(pattern);
          });
          list += newline;
        }
      }
    });

    // if (numberOfPatternsDetected <= 2) {
    //   list += newline + tab + this.formatMessage('  ') + newline;
    // }

    list += newline;
    return list;
  };
}

const getThoraxReport = (imagesInCase: ImageForReport[], intl: IntlShape) => {
  const thoraxReportGenerator = new ThoraxReportGenerator(intl);

  if (['fr', 'en'].includes(intl.locale) === false) {
    return thoraxReportGenerator.generateList(imagesInCase);
  }

  return thoraxReportGenerator.generateReport(imagesInCase);
};

export default getThoraxReport;
